import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import NotificationList from "./NotificationList";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import useEscapeKey from "../hooks/useEscapeKey";
import useDialogFocus from "../hooks/useDialogFocus";

/**
 * The bell's desktop panel — a right-edge drawer over the page, not a route.
 *
 * Notifications are a glance, not a destination: half of them are read and dismissed
 * without acting on anything, and sending someone to another page for that costs them the
 * page they were on. On mobile the bell still navigates to /notifications, where a 400px
 * panel would be the whole screen and a page is the honest shape.
 *
 * Same right-edge geometry and spring as RoleSwitchWidget, so the app has one drawer
 * behaviour rather than two that nearly match.
 */
const NotificationPanel = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  // The shared overlay behaviours — the handler that used to live inline here is now
  // useEscapeKey, so the rest of the app's overlays close on Escape too rather than only
  // this one, and none of them leave the page scrolling behind the backdrop.
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);
  const panelRef = useDialogFocus(isOpen);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="notification-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120]"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-panel-title"
            tabIndex={-1}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-[420px] bg-white rounded-l-3xl shadow-2xl flex flex-col focus:outline-none"
          >
            <div className="flex items-center justify-between px-6 pt-7 pb-4 flex-none">
              <h2 id="notification-panel-title" className="text-[18px] font-bold text-[#020618]">
                {t("notifications.title", "Мэдэгдэл")}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common.close", "Хаах")}
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <FiX size={18} className="text-slate-600" />
              </button>
            </div>

            {/* The list scrolls, the header does not — a long list should not push the close
                button off the top of the panel. */}
            <div className="flex-1 overflow-y-auto px-6 pb-8">
              <NotificationList onNavigate={onClose} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default NotificationPanel;
